package co.uk.sacursoftware.ssinventory.data.remote

import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.util.AppLogger
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.logging.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.Json

class SSInventoryApi(private val client: HttpClient) {

    private val baseUrl = "https://sacur-software.co.uk/ssinventoryapi.php"

    companion object {
        fun createDefaultClient(): HttpClient {
            return createHttpClient()
        }
    }

    // GET Actions
    suspend fun getSuppliers(): List<Supplier> =
        client.get(baseUrl) {
            parameter("action", "get_suppliers")
        }.body()

    suspend fun getItems(supplierId: String): List<Item> =
        client.get(baseUrl) {
            parameter("action", "get_items")
            parameter("supplier_id", supplierId)
        }.body()

    suspend fun getStockCounts(supplierId: String): List<StockCount> =
        client.get(baseUrl) {
            parameter("action", "get_counts")
            parameter("supplier_id", supplierId)
        }.body()

    suspend fun getStockCountDetails(countId: String): List<StockCountItem> =
        client.get(baseUrl) {
            parameter("action", "get_count_details")
            parameter("count_id", countId)
        }.body()

    suspend fun getOrders(supplierId: String): List<Order> =
        client.get(baseUrl) {
            parameter("action", "get_orders")
            parameter("supplier_id", supplierId)
        }.body()

    suspend fun getOrderItems(orderId: String): List<OrderItem> =
        client.get(baseUrl) {
            parameter("action", "get_order_items")
            parameter("order_id", orderId)
        }.body()

    suspend fun getReports(): List<Report> =
        client.get(baseUrl) {
            parameter("action", "get_reports")
        }.body()

    suspend fun getReportDetails(reportId: String): List<ReportDetail> =
        client.get(baseUrl) {
            parameter("action", "get_report_details")
            parameter("report_id", reportId)
        }.body()

    suspend fun getProfile(): List<ProfileItem> =
        try {
            client.get(baseUrl) {
                parameter("action", "get_profile")
            }.body()
        } catch (e: Exception) {
            emptyList()
        }

    suspend fun getImages(foreignId: String): List<AppImage> =
        try {
            client.get(baseUrl) {
                parameter("action", "get_images")
                parameter("foreign_id", foreignId)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }

    suspend fun getDaily(date: String): DailyRecord? =
        try {
            client.get(baseUrl) {
                parameter("action", "get_daily")
                parameter("date", date)
            }.body()
        } catch (e: Exception) {
            null
        }

    suspend fun getMasterPrepItems(): List<PrepItem> =
        try {
            client.get(baseUrl) {
                parameter("action", "get_master_prep_items")
            }.body()
        } catch (e: Exception) {
            emptyList()
        }

    suspend fun getPrepRoutines(dayOfWeek: String? = null): List<PrepRoutine> =
        try {
            client.get(baseUrl) {
                parameter("action", "get_prep_routines")
                if (dayOfWeek != null) {
                    parameter("day_of_week", dayOfWeek)
                }
            }.body()
        } catch (e: Exception) {
            emptyList()
        }

    suspend fun getPlannedPrepItems(date: String): List<PlannedPrepItem> =
        try {
            client.get(baseUrl) {
                parameter("action", "get_planned_prep_items")
                parameter("date", date)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }

    suspend fun getPrepItems(date: String): List<PlannedPrepItem> = getPlannedPrepItems(date)

    suspend fun getServices(): List<Service> =
        try {
            client.get(baseUrl) {
                parameter("action", "get_services")
            }.body()
        } catch (e: Exception) {
            emptyList()
        }

    suspend fun getTools(): List<ToolRequest> =
        try {
            client.get(baseUrl) {
                parameter("action", "get_tools")
            }.body()
        } catch (e: Exception) {
            emptyList()
        }

    suspend fun getTrainingCategories(): List<TrainingCategory> =
        try {
            client.get(baseUrl) {
                parameter("action", "get_training_categories")
            }.body()
        } catch (e: Exception) {
            emptyList()
        }

    suspend fun getTrainingItems(): List<TrainingItem> =
        try {
            client.get(baseUrl) {
                parameter("action", "get_training_items")
            }.body()
        } catch (e: Exception) {
            emptyList()
        }

    suspend fun getDeliveries(date: String): List<Delivery> =
        try {
            client.get(baseUrl) {
                parameter("action", "get_deliveries")
                parameter("date", date)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }

    // POST Actions
    suspend fun createSupplier(request: CreateSupplierRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun editSupplier(request: EditSupplierRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun createItem(request: CreateItemRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun editItem(request: EditItemRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun createStockCount(request: CreateStockCountRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun editStockCount(request: EditStockCountRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun deleteStockCount(countId: String): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(DeleteRequest(action = "delete_stock_count", id = countId))
        }.body()

    suspend fun createOrder(request: CreateOrderRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun editOrder(request: EditOrderRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun deleteOrder(orderId: String): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(DeleteRequest(action = "delete_order", id = orderId))
        }.body()

    suspend fun deleteReport(reportId: String): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(DeleteRequest(action = "delete_report", id = reportId))
        }.body()

    suspend fun saveService(request: SaveServiceRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun deleteService(id: String): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(DeleteServiceRequest(id = id))
        }.body()

    suspend fun saveTool(request: SaveToolRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun deleteTool(id: String): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(DeleteToolRequest(id = id))
        }.body()

    suspend fun saveTrainingCategory(request: SaveTrainingCategoryRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun deleteTrainingCategory(id: String): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(DeleteTrainingCategoryRequest(id = id))
        }.body()

    suspend fun saveTrainingItem(request: SaveTrainingItemRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun deleteTrainingItem(id: String): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(DeleteTrainingItemRequest(id = id))
        }.body()

    suspend fun getFileFolders(): List<FileFolder> =
        try {
            client.get(baseUrl) {
                parameter("action", "get_file_folders")
            }.body()
        } catch (e: Exception) {
            emptyList()
        }

    suspend fun getFileItems(): List<FileItem> =
        try {
            client.get(baseUrl) {
                parameter("action", "get_file_items")
            }.body()
        } catch (e: Exception) {
            emptyList()
        }

    suspend fun saveFileFolder(request: SaveFileFolderRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun deleteFileFolder(id: String): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(DeleteFileFolderRequest(id = id))
        }.body()

    suspend fun saveFileItem(request: SaveFileItemRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun deleteFileItem(id: String): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(DeleteFileItemRequest(id = id))
        }.body()

    suspend fun verifyReceipt(request: VerifyReceiptRequest): ReceiptVerificationResult =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun processSilentDelivery(request: ProcessSilentDeliveryRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun saveDelivery(request: SaveDeliveryRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun deleteDelivery(id: String): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(DeleteDeliveryRequest(id = id))
        }.body()

    suspend fun generateReport(request: GenerateReportRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun saveProfile(request: SaveProfileRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun saveDailyImages(request: SaveDailyImagesRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun getDailyRoutines(date: String? = null): List<DailyRoutine> =
        try {
            client.get(baseUrl) {
                parameter("action", "get_daily_routines")
                if (date != null) {
                    parameter("date", date)
                }
            }.body()
        } catch (e: Exception) {
            emptyList()
        }

    suspend fun saveDailyRoutine(request: SaveDailyRoutineRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun deleteDailyRoutine(id: String): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(DeleteDailyRoutineRequest(id = id))
        }.body()

    suspend fun toggleDailyRoutine(id: String, date: String): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(ToggleDailyRoutineRequest(id = id, date = date))
        }.body()

    suspend fun saveMasterPrepItem(request: SavePrepItemRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun deleteMasterPrepItem(id: String): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(DeletePrepItemRequest(id = id))
        }.body()

    suspend fun savePrepRoutine(request: SavePrepRoutineRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun deletePrepRoutine(id: String): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(DeletePrepRoutineRequest(id = id))
        }.body()

    suspend fun savePlannedPrepItem(request: SavePlannedPrepItemRequest): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun savePrepItem(request: SavePlannedPrepItemRequest): AppResponse = savePlannedPrepItem(request)

    suspend fun deletePlannedPrepItem(id: String): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(DeletePlannedPrepItemRequest(id = id))
        }.body()

    suspend fun deletePrepItem(id: String): AppResponse = deletePlannedPrepItem(id)

    suspend fun togglePlannedPrepItem(id: String): AppResponse =
        client.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(TogglePlannedPrepItemRequest(id = id))
        }.body()

    suspend fun togglePrepItem(id: String): AppResponse = togglePlannedPrepItem(id)
}

expect fun createHttpClient(): HttpClient

fun configureClient(client: HttpClient): HttpClient {
    return client.config {
        expectSuccess = false
        install(HttpTimeout) {
            requestTimeoutMillis = 40_000
            connectTimeoutMillis = 40_000
            socketTimeoutMillis = 40_000
        }
        install(DefaultRequest) {
            url {
                parameters.append("v", getDatabaseVariant())
            }
        }
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                prettyPrint = true
                isLenient = true
                encodeDefaults = true
            })
        }
        install(Logging) {
            level = LogLevel.ALL
            logger = object : Logger {
                override fun log(message: String) {
                    when {
                        message.contains("https://") -> AppLogger.d("EXACT URL: $message")
                        message.startsWith("{") || message.startsWith("[") -> AppLogger.d("JSON BODY: $message")
                        else -> AppLogger.d("Ktor: $message")
                    }
                }
            }
        }
    }
}
